/** In-memory page snapshots for instant revisit while a background refresh runs. */

export interface PageCacheEntry<T> {
  data: T;
  fetchedAt: number;
}

export const DEFAULT_PAGE_CACHE_FRESH_MS = 90_000;

const store = new Map<string, PageCacheEntry<unknown>>();
const listeners = new Set<(key: string) => void>();

function notify(key: string): void {
  Array.from(listeners).forEach((listener) => {
    listener(key);
  });
}

export function getPageCache<T>(key: string): PageCacheEntry<T> | undefined {
  return store.get(key) as PageCacheEntry<T> | undefined;
}

export function setPageCache<T>(key: string, data: T): void {
  store.set(key, { data, fetchedAt: Date.now() });
  notify(key);
}

export function clearPageCache(key?: string): void {
  if (key) {
    store.delete(key);
    notify(key);
    return;
  }
  store.clear();
  notify('*');
}

export function hasPageCache(key: string): boolean {
  return store.has(key);
}

export function isPageCacheFresh(
  key: string,
  freshMs: number = DEFAULT_PAGE_CACHE_FRESH_MS,
): boolean {
  const entry = store.get(key);
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < freshMs;
}

/**
 * Subscribe to cache writes/clears. Pass `key` to only hear about that entry
 * (plus global `*` clears). Omit `key` to hear every change.
 */
export function subscribePageCache(
  listener: (key: string) => void,
  key?: string,
): () => void {
  const wrapped = (changedKey: string) => {
    if (
      key != null &&
      changedKey !== key &&
      changedKey !== '*'
    ) {
      return;
    }
    listener(changedKey);
  };
  listeners.add(wrapped);
  return () => {
    listeners.delete(wrapped);
  };
}
